import { isNullOrUndefined } from "./validateObject";
import dayjs from "dayjs";
import "dayjs/locale/es-mx";

/**
 * Muestra un valor o un texto por defecto si es null/undefined.
 * @param {*} value - Valor a evaluar.
 * @param {object} [options] - Opciones de personalización.
 * @param {string} [options.fallback="N/D"] - Texto a mostrar si el valor es null/undefined.
 * @param {function} [options.formatter] - Función para transformar el valor antes de mostrarlo.
 * @returns {string|*}
 */
export const displayValue = (value, options = {}) => {
  const { fallback = "N/D", formatter } = options;
  if (isNullOrUndefined(value)) return fallback;
  return formatter ? formatter(value) : value;
};

/**
 * Muestra "Sí", "No" o un texto por defecto según un valor booleano.
 * Construido sobre displayValue con un formatter de booleano.
 * @param {*} value - Valor a evaluar.
 * @param {object} [options] - Opciones de personalización.
 * @param {string} [options.fallback="N/D"] - Texto a mostrar si el valor es null/undefined.
 * @param {string} [options.trueLabel="Sí"] - Texto para valores truthy.
 * @param {string} [options.falseLabel="No"] - Texto para valores falsy.
 * @returns {string}
 */
export const displayBoolean = (value, options = {}) => {
  const { trueLabel = "Sí", falseLabel = "No", fallback } = options;
  return displayValue(value, {
    fallback,
    formatter: (v) => (v ? trueLabel : falseLabel),
  });
};

/**
 * Formatea una fecha para presentación.
 * Construido sobre displayValue con un formatter de dayjs.
 * @param {*} value - Fecha a formatear (string ISO, Date, timestamp, etc.).
 * @param {object} [options] - Opciones de personalización.
 * @param {string} [options.fallback="N/D"] - Texto a mostrar si el valor es null/undefined o fecha inválida.
 * @param {string} [options.format="DD/MMM/YYYY"] - Formato de salida (sintaxis dayjs).
 * @param {string} [options.locale="es-mx"] - Locale para nombres de meses/días.
 * @returns {string}
 */
export const displayDate = (value, options = {}) => {
  const { format = "DD/MMM/YYYY", locale = "es-mx", fallback = "N/D" } = options;
  return displayValue(value, {
    fallback,
    formatter: (v) => {
      const date = dayjs(v).locale(locale);
      return date.isValid() ? date.format(format) : fallback;
    },
  });
};

/**
 * Formatea un número para presentación (separador de miles).
 * Construido sobre displayValue con un formatter numérico.
 * @param {*} value - Valor a formatear.
 * @param {object} [options] - Opciones de personalización.
 * @param {string} [options.fallback="N/D"] - Texto a mostrar si el valor es null/undefined o no numérico.
 * @param {string} [options.locale="en-US"] - Locale para toLocaleString.
 * @param {number} [options.minimumFractionDigits=0] - Mínimo de decimales.
 * @param {number} [options.maximumFractionDigits=2] - Máximo de decimales.
 * @returns {string}
 */
export const displayNumber = (value, options = {}) => {
  const {
    fallback = "N/D", locale = "en-US",
    minimumFractionDigits = 0, maximumFractionDigits = 2
  } = options;
  return displayValue(value, {
    fallback,
    formatter: (v) => {
      const number = Number(v);
      if (isNaN(number)) return fallback;
      return number.toLocaleString(locale, { minimumFractionDigits, maximumFractionDigits });
    },
  });
};
